import React, {useEffect, useState} from "react";
import {useAuthWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {useSearchParams} from "react-router-dom";
import {VsiBubble, VsiH4} from "../../../styled/App.styled";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import {Card, Button, Tooltip} from "reactstrap";
import Stockchart from "../../../components/Stockchart";
import Grid from "../../../components/Grid";
import {formatStockChartConfig} from "../../../utils/Helpers";
import {standardCapacityColors} from "../../../utils/chartUtils";
import {PostAuth, PutAuth, getReport} from "../../../utils/Verbs";
import {allFilesByShares} from "../../../utils/gridColumnDefs/files/fileTrends";
import styles from "../../../utils/themeStyles";

import { useVsiAppContext} from "../../../stores/VsiApp";

const chartMetrics = [
  {value: 'shares', label: 'Shares', route: 'file_trend_by_share_proc', yAxis: 'GiB'},
  // { value: 'ddr', label: 'Data Reduction Ratio', route: 'device_ddr_trend', yAxis: 'Ratio'},
];

export default function FileTrends({context, title}) {
  const { theme, client, periodId, user, getAccessTokenSilently } = useVsiAppContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const metric = searchParams.get('metric') || 'shares';
  let defaultMetric = chartMetrics.find(({value}) => metric === value) || chartMetrics[0];

  const [chartMetric, setChartMetric] = useState(defaultMetric);
  const [activeMetricSelection, setActiveMetricSelection] = useState(chartMetrics);
  const [isLoading, setIsLoading] = useState(true);

  const [chartData, setChartData] = useState([]);
  const [chartDefs, setChartDefs] = useState([]);
  const [formattedChartData, setFormattedChartData] = useState([]);
  const [chartConfig, setChartConfig] = useState(null);
  const [shareOptions, setShareOptions] = useState([]);
  const [data, setData] = useState([]);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const [additionalChartOption, setAdditionalChartOption] = useState([]);
  const [additionalChartOptionLabel, setAdditionalChartOptionLabel] = useState('');

  const [tooltipMessage, setTooltipMessage] = useState('Save List of Shares');

  const [savedChangesData] = useAuthWithRoute(`/api/v6/admin/file_shares_list/${user._id}/${client.clientId}/trends`);

  useTitleFromComponent(`${context} / ${title}`);

  useEffect(() => {
    let newChartMetric = chartMetrics.find(({value}) => value === chartMetric.value) || chartMetrics[0];
    setActiveMetricSelection(chartMetrics);

    searchParams.set('metric', newChartMetric.value);
    setSearchParams(searchParams, {replace: true});
    if (additionalChartOption.length === 0) {
      getRouteForSelection(newChartMetric.route);
    };
  }, [chartMetric, periodId, savedChangesData]);

  useEffect(() => {
    if (data?.data) {
      handleStateWithData(data?.data?.recordsets || []);
    };
  }, [additionalChartOption]);


  const getRouteForSelection = async route => {
    const routeToSearch = `/api/v6/report/${route}?client_id=${client.clientId}&period_id=${periodId}`;

    setIsLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const data = await getReport(routeToSearch, null, token);
      setData(data);

      handleStateWithData(data?.data?.recordsets || []);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    };
  };

  const setAllAdditionalParams = (selection, metric) => {
    if (additionalChartOptionLabel !== metric) {
      setAdditionalChartOptionLabel(metric);
    };

    if (additionalChartOptionLabel === '' || additionalChartOptionLabel !== metric) {
      setShareOptions(selection);
      if(savedChangesData[0]?.sharesList?.length > 0) {
        setAdditionalChartOption(savedChangesData[0].sharesList);
      } else {
        setAdditionalChartOption([selection[0], selection[1], selection[2], selection[3], selection[4]] || {});
      }
    };
  };

  const handleStateWithData = (data) => {
    let config = null;

    let remapData = (data[0] || []).map(datum => {
      return {value: datum.share_name, label: datum.share_name};
    });

    switch (chartMetric.value) {
      case "shares":

        if (savedChangesData?.[0]?.sharesList?.length && !additionalChartOption.length) {
          var chartOptions = savedChangesData[0].sharesList;
        } else if (additionalChartOption.length === 0) {
          chartOptions = [remapData[0], remapData[1], remapData[2], remapData[3], remapData[4]];
        } else {
          chartOptions = additionalChartOption;
        };
        var newData = [];
        for (let i = 0; i < data[1].length; i++) {
          for (let j = 0; j < chartOptions.length; j++) {
            if (data[1][i].share_name === chartOptions[j]?.value) {
              newData.push(data[1][i]);
            };
          };
        };

        if (newData[newData.length - 1]["used"]) {
          for (let i = 0; i < chartOptions.length; i++) {
            let sortedArr = [];
            for (let j = 0; j < newData.length; j++) {
              if (newData[j].share_name === chartOptions[i]?.value) {
                sortedArr.push(newData[j]);
              };
            };
            let currentDate = sortedArr[sortedArr.length - 1];
            let finDate = sortedArr[sortedArr.length - 2] || null;
            var growthRate = (finDate?.used - currentDate?.used) / currentDate?.used * 100;

            let forecastArr = [];

            if (growthRate) {
              // loop to create forecast for 6 months
              for (let i = 1; i <= 6; i++) {
                let forecastAmt = currentDate?.used + ((-growthRate / 100) * currentDate?.used * i);
                let forecastDate = new Date(currentDate.collection_date).setMonth(new Date(currentDate.collection_date).getMonth() + i);
                forecastArr.push({
                  share_name: currentDate.share_name,
                  collection_date: new Date(forecastDate).toISOString(),
                  used: forecastAmt
                });
              };

              newData = newData.concat(forecastArr);
            };
          };
        };

        let renamed = newData.map(datum => {
          return {name: datum.share_name, field: 'used', dataLabels: {format: '{point.y:,.0f}'}, tooltip: {pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'}}
        });

        config = formatStockChartConfig(newData, renamed, "share_name");
        setChartConfig({
          colors: standardCapacityColors,
          yAxis: {
            title: {
              text: 'GiB'
            },
          }
        });

        config.series = config.series.filter((value, i, array) =>
          i === array.findIndex((datum) => (
            datum.place === value.place && datum.name === value.name
          ))
        );
        setFormattedChartData(config.series.map(
          (item) => ({
            ...item,
            zoneAxis: "x",
            zones: [
              {
                value: Date.parse(
                  data?.[1]?.[data?.[1].length - 1]?.collection_date?.split?.("T")?.[0]
                ),
              },
              {
                dashStyle: "Dash",
              },
            ],
          })
        ) || []);

        setAllAdditionalParams(remapData, 'Shares');
        handleChartData(data?.[1] || []);
        break;
      default:
        // nothing
    };
  };

  const handleChartData = data => {
    let filtChartData = [];

    for (let i = 0; i < additionalChartOption.length; i++) {
      let tempData = data.filter(obj => {
        return obj.share_name === additionalChartOption[i]?.value
      });
      filtChartData = filtChartData.concat(tempData);
    };

    setChartData(filtChartData);
    setChartDefs(allFilesByShares);

  };

  const onMetricChange = async (metric) => {
    setChartMetric(metric);
    searchParams.set('metric', metric.value);
    setSearchParams(searchParams, {replace: true});
  };

  const onAdditionalOptionChange = async (option) => {
    setAdditionalChartOption(option);
  };

  const savePlan = async () => {
    let savedPlan = {
      user: user._id,
      clientId: client.clientId,
      type: 'trends',
      sharesList: additionalChartOption
    };
    const token = await getAccessTokenSilently();
    if (savedChangesData.length > 0) {
      if (savedChangesData[0].sharesList !== savedPlan.sharesList) {
        try {
          await PutAuth(`/api/v6/admin/file_shares_list/${savedChangesData[0]._id}`, savedPlan, token);
          setTooltipMessage('Saved!');
        } catch (err) {
          console.error(err);
        }
      };
    } else {
      try {
        await PostAuth(`/api/v6/admin/file_shares_list`, savedPlan, token);
        setTooltipMessage('Saved!');
      } catch (err) {
        console.error(err);
      }
    };
  };

  const filterChanged = (eventData) => {
    let rowData = [];
    eventData.api.forEachNodeAfterFilter(node => {
      rowData.push(node.data);
    });

    handleStateWithData(rowData || []);
  };

  return <Card className={`report-card p-2 nav-card-${theme}`}>
    <VsiH4>File Trends</VsiH4>
    <VsiBubble noAutobot>
    <div className={"w-100 p-2 d-flex"}>
    <VsiLabeledSelect
  options={activeMetricSelection}
  isLoading={isLoading}
  isDisabled={isLoading}
  label={'Metric'}
  minWidth={200}
  maxWidth={500}
  onChange={onMetricChange}
  defaultValue={chartMetric}
    />
    {additionalChartOptionLabel && (
      <>
      <VsiLabeledSelect
      options={shareOptions}
      isLoading={isLoading}
      isDisabled={isLoading}
      label="Shares"
      minWidth={200}
      maxWidth={600}
      onChange={onAdditionalOptionChange}
      defaultValue={additionalChartOption}
      isMulti
      />
      <Button className={"h-50 d-inline-block"} style={{marginTop: "38px"}} onClick={savePlan} id="saveSharesTooltip">
      <i className={`fa-solid fa-floppy-disk`}/>
      </Button>
      <Tooltip
      style={{color: styles[theme].textColor, backgroundColor: styles[theme].altBgColor}}
      isOpen={tooltipOpen}
      placement="right"
      target="saveSharesTooltip"
      toggle={() => { setTooltipOpen(!tooltipOpen);
        setTooltipMessage('Save List of Shares') }}>
      {tooltipMessage}
      </Tooltip>
      </>
    )}
    </div>
    </VsiBubble>
    <VsiBubble autobotInfo={{ secondaryMetric: additionalChartOption }}>
    <Stockchart
  series={formattedChartData}
  config={chartConfig}
  formatData={'{y:,2f}'}
  formatLabels={'{text}'}
  formatPoint={`{series.name}`}
    />
    </VsiBubble>
    <VsiBubble autobotInfo={{ secondaryMetric: additionalChartOption }}>
    <Grid
  addStyle
  data={chartData}
  columnDefs={chartDefs}
  saveParams={{metric: chartMetric.value}}
  filterChanged={filterChanged}
    />
    </VsiBubble>
    </Card>
}
